import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { PlayCircle, UserRound, AlertTriangle, Loader2, CalendarClock } from 'lucide-react';
import ProgrammeDialog from './ProgrammeDialog';
import SearchableSelect from '../common/SearchableSelect';
import { fetchStartOptions, startPatientProgramme } from '../../redux/programmeSlice';
import { getDoctorNameList } from '../../redux/doctorSlice';
import {
    byOffset, durationLine, offsetLine, startDateError, todayIso, dayNumber, LIMITS,
} from '../../utils/careProgrammes';
import { serverReason } from '../patientTabs/prescriptionHelpers';

// START PROGRAMME — pick one of the programmes this doctor may start (their
// own + the hospital's) and type its start date, labelled with the
// programme's own word for it (its `startLabel`: whatever the doctor called
// it). The server snapshots the programme and answers with the timeline.
//
// The hospital admin names the doctor it runs under (contract §3: defaults to
// the visit's doctor); the list of programmes is then THAT doctor's own + the
// hospital's (purpose=start&doctorId=). Programmes already running for this
// patient are shown but cannot be picked (409 PROGRAMME_ACTIVE otherwise).

const isoPlus = (iso, days) => {
    const n = dayNumber(iso);
    if (!Number.isFinite(n)) return '';
    const d = new Date((n + days) * 86400000);
    return d.toISOString().slice(0, 10);
};

function StartProgrammeModal({ isOpen, patientId, appointmentId = null, defaultDoctorId = null, runningIds = [], onClose }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const role = (typeof localStorage !== 'undefined' && localStorage.getItem('Role')) || '';
    const isAdmin = role === 'admin';
    const startOptions = useSelector((state) => state.programmes?.startOptions);
    const acting = useSelector((state) => state.programmes?.acting === 'start');
    const { doctorNameList, loadingList } = useSelector((state) => state.doctor || {});
    const [doctorId, setDoctorId] = useState('');
    const [programmeId, setProgrammeId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [showErrors, setShowErrors] = useState(false);
    const [serverError, setServerError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setProgrammeId('');
        setStartDate('');
        setShowErrors(false);
        setServerError('');
        setDoctorId(isAdmin ? (defaultDoctorId || '') : '');
        if (isAdmin) dispatch(getDoctorNameList());
    }, [isOpen, isAdmin, defaultDoctorId, dispatch]);

    useEffect(() => {
        if (!isOpen) return;
        dispatch(fetchStartOptions(isAdmin && doctorId ? { doctorId } : {}));
    }, [isOpen, isAdmin, doctorId, dispatch]);

    const scopeNow = isAdmin && doctorId ? doctorId : '';
    const options = startOptions?.scope === scopeNow ? startOptions : null;
    const all = useMemo(() => [...(options?.mine || []), ...(options?.hospital || [])], [options]);
    const chosen = all.find((p) => p.programmeId === programmeId) || null;
    const running = new Set(runningIds);
    const doctors = Array.isArray(doctorNameList) ? doctorNameList : [];
    const doctorOptions = doctors.map((d) => ({ value: d.doctorId, label: d.name || t('common.doctor_label', 'Doctor') }));

    const today = todayIso();
    const dateErr = startDateError(startDate, today);
    const errors = {
        programme: !chosen ? 'err_pick_programme' : '',
        startDate: chosen ? dateErr : '',
    };
    const invalid = Boolean(errors.programme || errors.startDate);

    const submit = async () => {
        setShowErrors(true);
        if (invalid || acting) return;
        setServerError('');
        const res = await dispatch(startPatientProgramme({
            patientId,
            programmeId: chosen.programmeId,
            startDate,
            appointmentId,
            doctorId: isAdmin ? (doctorId || null) : null,
        }));
        if (startPatientProgramme.fulfilled.match(res)) {
            toast.success(t('programmes.started', { name: chosen.name }));
            onClose?.();
            return;
        }
        setServerError(serverReason(res.payload, t('programmes.start_failed'), i18n.language));
    };

    const label = chosen?.startLabel || t('programmes.start_date');
    const group = (title, list) => (list.length > 0 && (
        <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{title}</p>
            <div className="flex flex-col gap-1.5">
                {list.map((p) => {
                    const isRunning = running.has(p.programmeId);
                    const on = p.programmeId === programmeId;
                    return (
                        <label
                            key={p.programmeId}
                            data-testid={`start-programme-option-${p.programmeId}`}
                            className={`flex cursor-pointer items-start gap-2 rounded-xl border px-3 py-2 transition-colors ${on ? 'border-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-100 hover:border-[#4ABDBD]/40'} ${isRunning ? 'cursor-not-allowed opacity-60' : ''}`}
                        >
                            <input
                                type="radio"
                                name="start-programme"
                                value={p.programmeId}
                                checked={on}
                                disabled={isRunning}
                                onChange={() => { setProgrammeId(p.programmeId); setServerError(''); }}
                                className="mt-1 h-4 w-4 accent-[#4ABDBD]"
                            />
                            <span className="min-w-0">
                                <span className="block text-sm font-semibold text-gray-800">{p.name}</span>
                                <span className="block text-[11px] text-gray-500">
                                    {durationLine(p.durationDays, t)} · {t('programmes.milestones_count', { n: (p.milestones || []).length })}
                                    {isRunning ? ` · ${t('programmes.already_running')}` : ''}
                                </span>
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    ));

    return (
        <ProgrammeDialog
            isOpen={isOpen}
            testId="start-programme"
            title={t('programmes.start_title')}
            icon={PlayCircle}
            onClose={onClose}
            onSubmit={submit}
            submitLabel={acting ? t('programmes.starting') : t('programmes.start_programme')}
            busy={acting}
        >
            {isAdmin && (
                <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-gray-600">{t('programmes.runs_under')}</span>
                    <SearchableSelect
                        testId="start-programme-doctor"
                        value={doctorId}
                        onChange={(v) => { setDoctorId(v || ''); setProgrammeId(''); }}
                        options={doctorOptions}
                        placeholder={loadingList ? t('programmes.loading') : t('programmes.pick_doctor')}
                        searchPlaceholder={t('programmes.search_doctor')}
                        icon={UserRound}
                        disabled={loadingList}
                        variant="form"
                    />
                    <p className="text-[11px] text-gray-400">{t('programmes.runs_under_hint')}</p>
                </div>
            )}

            {(!options || (options.loading && !options.loaded)) && (
                <p className="flex items-center gap-2 text-sm text-[#4ABDBD]">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t('programmes.loading')}
                </p>
            )}
            {options?.error && (
                <p role="alert" className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                    {serverReason(options.error, t('programmes.load_failed'), i18n.language)}
                </p>
            )}
            {options?.loaded && !options.error && all.length === 0 && (
                <p data-testid="start-programme-none" className="rounded-xl bg-gray-50 px-3 py-4 text-center text-sm text-gray-500">
                    {role === 'doctor' ? t('programmes.none_to_start_doctor') : t('programmes.none_to_start')}
                </p>
            )}
            {options?.loaded && !options.error && all.length > 0 && (
                <div className="space-y-3" data-testid="start-programme-options">
                    {group(isAdmin ? t('programmes.doctor_own_group') : t('programmes.my_programmes'), options.mine || [])}
                    {group(t('programmes.hospital_programmes'), options.hospital || [])}
                </div>
            )}
            {showErrors && errors.programme && (
                <p data-testid="start-programme-pick-error" className="text-xs text-red-600">{t(`programmes.${errors.programme}`)}</p>
            )}

            {chosen && (
                <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                    <label htmlFor="start-programme-date" className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                        <CalendarClock className="h-4 w-4 text-[#2D8A8A]" aria-hidden="true" />
                        {label}
                    </label>
                    <input
                        id="start-programme-date"
                        data-testid="start-programme-date"
                        type="date"
                        value={startDate}
                        min={isoPlus(today, -LIMITS.startPastDays)}
                        max={isoPlus(today, LIMITS.startFutureDays)}
                        onChange={(e) => { setStartDate(e.target.value); setServerError(''); }}
                        className={`w-full rounded-xl border bg-white px-3 py-2 text-sm focus:border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 ${showErrors && errors.startDate ? 'border-red-300' : 'border-gray-200'}`}
                    />
                    {showErrors && errors.startDate
                        ? <p data-testid="start-programme-date-error" className="mt-1 text-xs text-red-600">{t(`programmes.${errors.startDate}`)}</p>
                        : <p className="mt-1 text-[11px] text-gray-400">{t('programmes.start_date_hint', { label })}</p>}
                    <ol className="mt-3 flex flex-col gap-1 border-l-2 border-[#4ABDBD]/25 pl-3" data-testid="start-programme-preview">
                        {byOffset(chosen.milestones).map((m) => (
                            <li key={m.key} className="text-[11px] text-gray-600">
                                <span className="font-semibold text-gray-700">{m.name}</span>
                                <span className="text-gray-400"> · {offsetLine(m, t)}</span>
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {serverError && (
                <p role="alert" data-testid="start-programme-server-error" className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 p-3 text-xs font-medium text-red-700">
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {serverError}
                </p>
            )}
        </ProgrammeDialog>
    );
}

export default StartProgrammeModal;
